import { Container, Graphics } from 'pixi.js';
import { darken, lighten, mix, type ThemeArt } from './theme-art';
import { bodyRect, bodyEllipse } from './shading';

/**
 * CASTLE HEARTHHOLD — the shared procedural ART KIT (ADR-0051). Every bespoke medieval piece the room
 * decorators compose lives here, drawn in the SAME convention as the office furniture catalog: a Pixi
 * {@link Container} in LOCAL space (footprint centred on its placement point, body rising above it), with
 * a soft contact shadow, the unified TOP-LEFT key light (see {@link './shading'}), and everything themed
 * off {@link ThemeArt} so the keep re-skins with the palette. Built ONCE on map build/re-skin — never per
 * frame, never allocating in the loop (Performance-Engineer gate).
 *
 * Light is the soul of this world (the engine is flat 2D — "awe" comes from light + colour, not height).
 * So the lighting props (torch · brazier · hearth · forge · candelabra · stained glass) each lay a cheap
 * STATIC warm glow pool on the floor — the three big warm pools (Hearth Hall, Smithy forge, Kitchen
 * cauldron) are what make the stronghold read as alive even at low population.
 *
 * Palette helpers (all theme-reactive so a re-skin flows through): warm honey STONE, oak WOOD, dark IRON,
 * bright GOLD, and the FLAME ramp. Keep the honeyed warmth dominant over the stone grey — the castle's
 * storybook soul depends on it (never let it desaturate to cold concrete).
 */

// ── Palette (theme-reactive) ────────────────────────────────────────────────────────────────────────
const stone = (art: ThemeArt): number => mix(0x6f6554, art.wall, 0.5);
const stoneLo = (art: ThemeArt): number => darken(stone(art), 0.16);
const stoneHi = (art: ThemeArt): number => lighten(stone(art), 0.12);
const wood = (art: ThemeArt): number => mix(0x6a4326, art.wall, 0.14);
const woodDk = (art: ThemeArt): number => darken(wood(art), 0.22);
const iron = (): number => 0x33333c;
const gold = (art: ThemeArt): number => mix(0xd9a63a, art.accent, 0.5);
const FLAME_OUT = 0xff7a2a;
const FLAME_MID = 0xffc24a;
const FLAME_CORE = 0xfff0b8;

/** A soft elliptical contact shadow so a piece reads as resting on the floor (not floating). */
function shadow(w: number, h: number, y = 0): Graphics {
  return new Graphics().ellipse(0, y, w, h).fill({ color: 0x000000, alpha: 0.22 });
}

/** A static warm GLOW POOL on the floor (additive-feel: stacked low-alpha warm fills), scaled per theme. */
export function glowPool(c: Container, x: number, y: number, r: number, art: ThemeArt, k = 1): void {
  const a = art.glowStrength * k;
  c.addChild(
    new Graphics()
      .ellipse(x, y, r, r * 0.58)
      .fill({ color: art.glow, alpha: 0.06 * a + 0.03 })
      .ellipse(x, y, r * 0.62, r * 0.36)
      .fill({ color: art.glow, alpha: 0.09 * a + 0.04 })
      .ellipse(x, y, r * 0.3, r * 0.18)
      .fill({ color: lighten(art.glow, 0.25), alpha: 0.12 * a + 0.05 }),
  );
}

/** A little upward flame (base at y, licks rising above it). Three stacked teardrops orange→core. */
function flame(g: Graphics, x: number, y: number, s: number): void {
  g.ellipse(x, y - s * 0.5, s * 0.62, s * 1.0).fill({ color: FLAME_OUT, alpha: 0.95 });
  g.ellipse(x, y - s * 0.35, s * 0.4, s * 0.7).fill({ color: FLAME_MID, alpha: 0.95 });
  g.ellipse(x, y - s * 0.22, s * 0.2, s * 0.42).fill({ color: FLAME_CORE, alpha: 0.95 });
}

// ── Lighting & atmosphere ─────────────────────────────────────────────────────────────────────────

/** A bracketed WALL TORCH: an iron sconce on the wall, a small flame, and a soft glow spill. Hang flush
 *  against a wall (the bracket points down-left). Cheap warm point-light — capped per room. */
export function wallTorch(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 8, 34, art, 0.9);
  const g = new Graphics();
  // iron bracket + cup
  g.moveTo(0, 6).lineTo(-6, -2).lineTo(-3, -10).stroke({ color: iron(), width: 3 });
  g.roundRect(-8, -16, 16, 8, 3).fill(iron()).roundRect(-8, -16, 16, 3, 3).fill({ color: lighten(iron(), 0.25), alpha: 0.6 });
  c.addChild(g);
  const f = new Graphics();
  flame(f, 0, -16, 11);
  c.addChild(f);
  return c;
}

/** A free-standing IRON BRAZIER: a tripod bowl of fire — the workhorse room warmth. Bigger glow than a
 *  wall torch; stands on the floor (contact shadow). */
export function brazier(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 4, 52, art, 1);
  c.addChild(shadow(20, 7, 14));
  const g = new Graphics();
  // three legs
  g.moveTo(-12, 14).lineTo(-4, -2).moveTo(12, 14).lineTo(4, -2).moveTo(0, 16).lineTo(0, -2).stroke({ color: iron(), width: 3 });
  // bowl
  bodyEllipse(g, 0, -4, 18, 8, darken(iron(), 0.1), true);
  g.ellipse(0, -6, 14, 5).fill({ color: 0x1a1a1f, alpha: 0.9 });
  // glowing coals
  g.ellipse(0, -6, 12, 4).fill({ color: FLAME_OUT, alpha: 0.55 });
  c.addChild(g);
  const f = new Graphics();
  flame(f, -5, -8, 9);
  flame(f, 5, -8, 10);
  flame(f, 0, -10, 13);
  c.addChild(f);
  return c;
}

/** Warm BRASS metal for candle fittings (never cold black iron) — candelabra/chandelier/sconces. */
const brass = (art: ThemeArt): number => mix(0x9a7838, art.accent, 0.42);

/** A standing CANDELABRA: a warm BRASS stand of three candles + a soft glow pool. Cosy point-light. */
export function candelabra(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 6, 34, art, 0.95);
  c.addChild(shadow(12, 4, 16));
  const b = brass(art);
  const g = new Graphics();
  g.rect(-3, -18, 6, 34).fill(b).ellipse(0, 17, 10, 4).fill(darken(b, 0.2)); // thick pole + base
  g.rect(-3, -18, 6, 4).fill({ color: 0xffffff, alpha: 0.18 });
  g.moveTo(-13, -16).lineTo(13, -16).stroke({ color: b, width: 3 }); // arm
  for (const cx of [-13, 0, 13]) g.circle(cx, -16, 2.4).fill(b); // cups
  c.addChild(g);
  const f = new Graphics();
  for (const cx of [-13, 0, 13]) {
    f.roundRect(cx - 2, -27, 4, 11, 1.5).fill(0xf3e6c0);
    flame(f, cx, -27, 6);
  }
  c.addChild(f);
  return c;
}

/** A hanging BRASS CHANDELIER — a warm ring of candles on chains. Add high in a room; lays a soft floor
 *  glow below + reads as overhead light. */
export function chandelier(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 0, 78, art, 1);
  const b = brass(art);
  const g = new Graphics();
  // chains up to the ceiling
  for (const dx of [-22, 22]) g.moveTo(dx, -6).lineTo(0, -34).stroke({ color: darken(b, 0.2), width: 1.5, alpha: 0.5 });
  g.ellipse(0, 0, 34, 18).stroke({ color: b, width: 5 });
  g.ellipse(0, 0, 34, 18).stroke({ color: lighten(b, 0.3), width: 2, alpha: 0.7 });
  g.circle(0, 0, 4).fill(b);
  c.addChild(g);
  const f = new Graphics();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const x = Math.cos(a) * 34;
    const y = Math.sin(a) * 18;
    f.roundRect(x - 1.8, y - 9, 3.6, 9, 1).fill(0xf3e6c0);
    flame(f, x, y - 9, 5);
  }
  c.addChild(f);
  return c;
}

/** A bank of VOTIVE CANDLES on a low iron stand (the Chapel's gentle warmth). */
export function votiveStand(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 4, 30, art, 0.7);
  c.addChild(shadow(16, 5, 12));
  const g = new Graphics();
  bodyRect(g, -16, 2, 32, 10, 2, iron());
  c.addChild(g);
  const f = new Graphics();
  for (const [cx, h] of [[-12, 8], [-4, 12], [4, 10], [12, 7]] as const) {
    f.roundRect(cx - 1.8, 2 - h, 3.6, h, 1).fill(0xf3e6c0);
    flame(f, cx, 2 - h, 4.5);
  }
  c.addChild(f);
  return c;
}

// ── Architecture ────────────────────────────────────────────────────────────────────────────────────

/** A round STONE PILLAR (colonnade column) with a base + capital and a soft cast shadow. */
export function pillar(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(20, 7, 16));
  const g = new Graphics();
  // capital + base flares
  bodyRect(g, -16, -2, 32, 8, 2, stoneLo(art));
  bodyRect(g, -18, 6, 36, 8, 2, stoneLo(art));
  // shaft
  bodyRect(g, -13, -54, 26, 60, 4, stone(art));
  // fluting hints
  g.rect(-7, -50, 1.5, 52).fill({ color: stoneLo(art), alpha: 0.5 }).rect(5.5, -50, 1.5, 52).fill({ color: stoneLo(art), alpha: 0.5 });
  g.rect(-13, -54, 4, 60).fill({ color: 0xffffff, alpha: 0.07 });
  // capital top
  bodyRect(g, -17, -62, 34, 10, 2, stoneHi(art));
  c.addChild(g);
  return c;
}

/** A tall STAINED-GLASS pointed-arch window set into the wall: leaded panes of warm amber/rose/royal-blue
 *  lozenges, casting a coloured light pool on the floor below. Hang against a wall. */
export function stainedGlass(art: ThemeArt, w = 60, h = 120): Container {
  const c = new Container();
  // coloured floor pool from the light coming through
  c.addChild(
    new Graphics().ellipse(0, h * 0.5 + 14, w * 0.7, 18).fill({ color: mix(art.accent, 0xff7ac0, 0.3), alpha: 0.1 }),
  );
  const g = new Graphics();
  // stone arch surround (pointed)
  const top = -h / 2;
  g.moveTo(-w / 2 - 6, h / 2)
    .lineTo(-w / 2 - 6, top + 14)
    .quadraticCurveTo(0, top - 20, w / 2 + 6, top + 14)
    .lineTo(w / 2 + 6, h / 2)
    .fill(stoneLo(art));
  // glass field (pointed) — a deep WARM ember ground (not cool slate-blue) so the window reads warm
  g.moveTo(-w / 2, h / 2 - 4)
    .lineTo(-w / 2, top + 16)
    .quadraticCurveTo(0, top - 12, w / 2, top + 16)
    .lineTo(w / 2, h / 2 - 4)
    .fill(0x3a2218);
  // coloured lozenges (leaded) — WARM-dominant heraldic glass (ruby · amber · deep sapphire), NO green
  const cols = [0x8a2230, 0xb8842c, 0x33508a];
  for (let r = 0; r < 5; r++) {
    for (let q = -1; q <= 1; q++) {
      const yy = top + 24 + r * (h - 30) / 5;
      const xx = q * (w / 3.2);
      g.poly([xx, yy - 9, xx + 9, yy, xx, yy + 9, xx - 9, yy]).fill({ color: cols[(r + q + 5) % cols.length]!, alpha: 0.82 });
    }
  }
  // lead came + a glassy sheen
  g.rect(-1, top + 8, 2, h - 12).fill({ color: 0x1a1a22, alpha: 0.5 });
  g.poly([-w / 2 + 4, top + 18, -w / 6, top + 16, -w / 2 + 4, h / 4]).fill({ color: 0xffffff, alpha: 0.1 });
  c.addChild(g);
  return c;
}

/** The grand heraldic ROSE WINDOW — a circular leaded window in the guild's colours, the hero behind the
 *  throne. Casts a coloured pool below. */
export function roseWindow(art: ThemeArt, r = 70): Container {
  const c = new Container();
  c.addChild(new Graphics().ellipse(0, r + 18, r * 0.9, 20).fill({ color: mix(art.accent, 0xc8902f, 0.4), alpha: 0.12 }));
  const g = new Graphics();
  g.circle(0, 0, r + 8).fill(stoneLo(art));
  g.circle(0, 0, r).fill(0x2a1a12); // deep warm ember ground (not cool blue) behind the leaded wedges
  // WARM-DOMINANT heraldic glass: ruby + amber with a single deep-sapphire accent, NO green/teal, lower
  // saturation + heavy dark leading — a regal warm rose, never a full-spectrum pinwheel.
  const cols = [0x8a2230, 0xb8842c, 0x8a2230, 0x33508a];
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    g.poly([0, 0, Math.cos(a - 0.26) * r, Math.sin(a - 0.26) * r, Math.cos(a + 0.26) * r, Math.sin(a + 0.26) * r]).fill({
      color: cols[i % cols.length]!,
      alpha: 0.6,
    });
  }
  g.circle(0, 0, r * 0.28).fill({ color: gold(art), alpha: 0.95 });
  g.circle(0, 0, r * 0.28).stroke({ color: 0x1a1a22, width: 2 });
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    g.moveTo(0, 0).lineTo(Math.cos(a) * r, Math.sin(a) * r).stroke({ color: 0x1a1a22, width: 1.5, alpha: 0.6 });
  }
  g.circle(0, 0, r).stroke({ color: stoneHi(art), width: 3, alpha: 0.7 });
  c.addChild(g);
  return c;
}

/** A hanging heraldic BANNER / PENNANT in a guild colour, with a crest dab; gently tapered. Hang high on a
 *  wall or between pillars. `color` defaults to the theme accent (gold). */
export function banner(art: ThemeArt, color?: number, len = 90): Container {
  const c = new Container();
  const cloth = color ?? mix(0x8a2f2f, art.accent, 0.15);
  const g = new Graphics();
  // pole/rod
  g.rect(-26, -6, 52, 4, ).fill(iron());
  // cloth (tapered to a point, swallow-tail)
  g.moveTo(-20, -4)
    .lineTo(20, -4)
    .lineTo(20, len - 16)
    .lineTo(10, len - 16)
    .lineTo(0, len)
    .lineTo(-10, len - 16)
    .lineTo(-20, len - 16)
    .fill(cloth);
  g.moveTo(-20, -4).lineTo(20, -4).lineTo(20, 6).lineTo(-20, 6).fill({ color: 0xffffff, alpha: 0.12 });
  // gold trim + crest
  g.rect(-20, -4, 40, 3).fill({ color: gold(art), alpha: 0.9 });
  g.circle(0, len * 0.42, 11).fill({ color: gold(art), alpha: 0.9 }).circle(0, len * 0.42, 7).fill({ color: darken(cloth, 0.2) });
  g.poly([0, len * 0.32, 6, len * 0.46, 0, len * 0.52, -6, len * 0.46]).fill({ color: gold(art), alpha: 0.85 });
  c.addChild(g);
  return c;
}

/** A woven WALL TAPESTRY — a large hanging cloth with a simple scene/border (the War-Room campaign map,
 *  the Chapel devotional cloth, decorative scenes). */
export function tapestry(art: ThemeArt, w = 90, h = 120, scene: 'map' | 'scene' | 'crest' = 'scene'): Container {
  const c = new Container();
  const cloth = mix(0x5a4a36, art.wall, 0.25);
  const g = new Graphics();
  g.rect(-w / 2 - 4, -6, w + 8, 5).fill(woodDk(art)); // rod
  bodyRect(g, -w / 2, 0, w, h, 3, cloth);
  g.rect(-w / 2, h - 6, w, 8).fill(darken(cloth, 0.2)); // fringe
  for (let i = 0; i < Math.floor(w / 8); i++) g.rect(-w / 2 + i * 8 + 2, h + 1, 2, 6).fill(darken(cloth, 0.25));
  // border
  g.rect(-w / 2 + 6, 6, w - 12, h - 16).stroke({ color: gold(art), width: 2, alpha: 0.6 });
  if (scene === 'map') {
    g.rect(-w / 2 + 12, 16, w - 24, h - 34).fill({ color: mix(0x7a6a4a, art.wall, 0.2), alpha: 0.7 });
    g.moveTo(-w / 4, 30).bezierCurveTo(0, h / 2, -w / 6, h * 0.7, w / 5, h - 24).stroke({ color: 0x3a6ac2, width: 2, alpha: 0.7 });
    for (const [mx, my] of [[-w / 5, h * 0.4], [w / 6, h * 0.6], [0, h * 0.78]] as const) g.circle(mx, my, 3).fill({ color: 0xc24a3a, alpha: 0.9 });
  } else if (scene === 'crest') {
    g.poly([0, 18, w / 4, 30, w / 4, h * 0.6, 0, h - 22, -w / 4, h * 0.6, -w / 4, 30]).fill({ color: mix(0x8a2f2f, art.accent, 0.1), alpha: 0.85 });
    g.circle(0, h * 0.45, 10).fill({ color: gold(art), alpha: 0.9 });
  } else {
    // a simple woven "scene": hills + a sun, low-alpha
    g.circle(w / 5, h / 4, 8).fill({ color: gold(art), alpha: 0.8 });
    g.rect(-w / 2 + 12, h * 0.55, w - 24, h * 0.3).fill({ color: mix(0x4aa05a, art.wall, 0.2), alpha: 0.55 });
    g.ellipse(-w / 6, h * 0.6, w / 5, 16).fill({ color: mix(0x3a7a4a, art.wall, 0.2), alpha: 0.6 });
  }
  c.addChild(g);
  return c;
}

/** A round/heraldic SHIELD — wall decor + the trophy marker. `color` is the field; a boss + rim in gold. */
export function shield(art: ThemeArt, color?: number): Container {
  const c = new Container();
  const field = color ?? mix(0x3a6ac2, art.accent, 0.1);
  const g = new Graphics();
  g.moveTo(-18, -20).lineTo(18, -20).lineTo(18, 6).quadraticCurveTo(18, 24, 0, 30).quadraticCurveTo(-18, 24, -18, 6).fill(field);
  g.moveTo(-18, -20).lineTo(18, -20).lineTo(18, -8).lineTo(-18, -8).fill({ color: 0xffffff, alpha: 0.12 });
  g.moveTo(-18, -20).lineTo(18, -20).lineTo(18, 6).quadraticCurveTo(18, 24, 0, 30).quadraticCurveTo(-18, 24, -18, 6).stroke({ color: gold(art), width: 2.5 });
  // a chevron + boss
  g.moveTo(-12, 2).lineTo(0, -8).lineTo(12, 2).stroke({ color: gold(art), width: 2.5, alpha: 0.9 });
  g.circle(0, 12, 4).fill({ color: gold(art), alpha: 0.95 });
  c.addChild(g);
  return c;
}

/** A TROPHY SHIELD unit — a mounted shield over crossed banners (one per shipped "raid boss"/release). */
export function trophyShield(art: ThemeArt, color?: number): Container {
  const c = new Container();
  const g = new Graphics();
  // crossed banner staves
  g.moveTo(-22, 34).lineTo(18, -22).moveTo(22, 34).lineTo(-18, -22).stroke({ color: woodDk(art), width: 3 });
  for (const s of [-1, 1] as const) {
    g.moveTo(s * 14, -18).lineTo(s * 26, -14).lineTo(s * 24, -2).lineTo(s * 12, -6).fill({ color: mix(0x8a2f2f, art.accent, 0.1), alpha: 0.9 });
  }
  c.addChild(g);
  c.addChild(shield(art, color));
  return c;
}

/** A standing SUIT OF ARMOUR — a polished plate sentinel, flanking the gatehouse / smithy. */
export function suitOfArmour(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 30));
  const plate = mix(0x9aa3b0, art.accent, 0.12);
  const g = new Graphics();
  // legs
  bodyRect(g, -10, 8, 8, 24, 3, plate, true);
  bodyRect(g, 2, 8, 8, 24, 3, plate, true);
  // torso (breastplate)
  g.moveTo(-13, -16).lineTo(13, -16).lineTo(10, 12).lineTo(-10, 12).fill(plate);
  g.moveTo(-13, -16).lineTo(13, -16).lineTo(11, -8).lineTo(-11, -8).fill({ color: 0xffffff, alpha: 0.22 });
  g.moveTo(0, -14).lineTo(0, 10).stroke({ color: darken(plate, 0.25), width: 1.5 });
  // pauldrons + arms
  g.ellipse(-13, -14, 6, 5).fill(plate).ellipse(13, -14, 6, 5).fill(plate);
  bodyRect(g, -16, -10, 6, 22, 3, darken(plate, 0.06));
  bodyRect(g, 10, -10, 6, 22, 3, darken(plate, 0.06));
  // helm with plume
  g.ellipse(0, -24, 8, 9).fill(plate).ellipse(-2, -26, 4, 5).fill({ color: 0xffffff, alpha: 0.25 });
  g.rect(-1.5, -22, 3, 6).fill(0x1a1a22); // visor slit
  g.moveTo(0, -33).quadraticCurveTo(8, -34, 6, -22).stroke({ color: mix(0xc23a3a, art.accent, 0.1), width: 4 }); // plume
  c.addChild(g);
  // a round shield on the other arm + a grounded spear/halberd, so the sentinel reads unmistakably as ARMOUR
  c.addChild(new Graphics().moveTo(18, -34).lineTo(18, 30).stroke({ color: woodDk(art), width: 3 }).poly([18, -42, 22, -32, 14, -32]).fill(lighten(plate, 0.2)));
  c.addChild(new Graphics().ellipse(-18, 2, 7, 9).fill(mix(0x3a6ac2, art.accent, 0.1)).ellipse(-18, 2, 7, 9).stroke({ color: gold(art), width: 1.5, alpha: 0.7 }));
  c.scale.set(1.22); // scaled up so the sentinel reads as armour, not a small statue, at world scale
  return c;
}

/** A wall WEAPON / SHIELD RACK — swords, spears and a round shield (Smithy + Barracks). */
export function weaponRack(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(26, 6, 26));
  const g = new Graphics();
  bodyRect(g, -28, 18, 56, 8, 2, woodDk(art)); // base
  bodyRect(g, -28, -34, 56, 6, 2, wood(art)); // top rail
  const steel = mix(0x9aa3b0, art.accent, 0.1);
  // two spears
  for (const sx of [-20, -12]) {
    g.moveTo(sx, -30).lineTo(sx, 20).stroke({ color: woodDk(art), width: 3 });
    g.poly([sx, -40, sx + 4, -30, sx - 4, -30]).fill(steel);
  }
  // a sword
  g.moveTo(2, 16).lineTo(2, -26).stroke({ color: steel, width: 4 });
  g.moveTo(-6, -22).lineTo(10, -22).stroke({ color: gold(art), width: 3 });
  g.circle(2, 16, 3).fill(gold(art));
  // an axe
  g.moveTo(16, 18).lineTo(16, -24).stroke({ color: woodDk(art), width: 3 });
  g.moveTo(16, -22).quadraticCurveTo(28, -18, 24, -6).quadraticCurveTo(18, -10, 16, -10).fill(steel);
  c.addChild(g);
  return c;
}

// ── Hearth / forge / kitchen (the warm pools) ─────────────────────────────────────────────────────

/** THE GREAT HEARTH — the colossal round/horseshoe stone hearth at the heart of the Hall: the brightest
 *  warm-light pool on the map. Living flame + a wide ember glow. The gather magnet (non-interactive; the
 *  feast benches around it are where ducks sit). */
export function greatHearth(art: ThemeArt): Container {
  const c = new Container();
  // The single BRIGHTEST warm pool in the whole castle — a wide soft pool + a hot core.
  glowPool(c, 0, 0, 240, art, 1.6);
  glowPool(c, 0, -4, 120, art, 1.3);
  c.addChild(shadow(118, 36, 26));
  const g = new Graphics();
  // broad horseshoe stone base
  bodyEllipse(g, 0, 12, 108, 42, stone(art));
  g.ellipse(0, 6, 90, 32).fill(stoneLo(art));
  // tall chimney hood rising at the back + a gold mantel band
  g.moveTo(-82, -8).quadraticCurveTo(0, -100, 82, -8).lineTo(60, -8).quadraticCurveTo(0, -76, -60, -8).fill(stone(art));
  g.moveTo(-82, -8).quadraticCurveTo(0, -100, 82, -8).quadraticCurveTo(0, -84, -82, -8).fill({ color: 0xffffff, alpha: 0.07 });
  g.moveTo(-74, -10).quadraticCurveTo(0, -86, 74, -10).stroke({ color: gold(art), width: 3, alpha: 0.5 });
  // deep ember fire bed (warm, never pure black)
  g.ellipse(0, 4, 74, 24).fill({ color: 0x2b1c12 });
  g.ellipse(0, 4, 64, 19).fill({ color: FLAME_OUT, alpha: 0.6 });
  // logs
  for (const lx of [-36, -8, 24]) g.roundRect(lx, 0, 30, 9, 4).fill(woodDk(art));
  c.addChild(g);
  // a tall blazing fire (bright central tongue, taller than anything else on the map)
  const f = new Graphics();
  flame(f, -28, 2, 20);
  flame(f, 26, 2, 22);
  flame(f, -11, -3, 28);
  flame(f, 12, -3, 30);
  flame(f, 0, -10, 42);
  c.addChild(f);
  // drifting embers
  c.addChild(
    new Graphics()
      .circle(0, -46, 2.4).fill({ color: FLAME_CORE, alpha: 0.85 })
      .circle(-16, -38, 1.8).fill({ color: FLAME_MID, alpha: 0.7 })
      .circle(18, -40, 1.8).fill({ color: FLAME_MID, alpha: 0.7 }),
  );
  return c;
}

/** A SMALLER HEARTH / FIREPLACE (kitchen cooking-hearth, tavern/barracks fireside). `scale` ~0.6 default
 *  of the great hearth; warm glow pool + flame. Sits against a wall (the hood rises at the back). */
export function fireplace(art: ThemeArt, w = 90): Container {
  const c = new Container();
  glowPool(c, 0, 2, w * 0.95, art, 1);
  c.addChild(shadow(w * 0.5, 12, 14));
  const g = new Graphics();
  const half = w / 2;
  // stone surround + mantel
  bodyRect(g, -half, -54, w, 58, 4, stone(art));
  bodyRect(g, -half - 6, -60, w + 12, 10, 3, stoneHi(art)); // mantel
  // arched fire ALCOVE: a warm DEEP-EMBER interior (never pure black) framed by the stone
  g.moveTo(-half + 12, 4).lineTo(-half + 12, -28).quadraticCurveTo(0, -48, half - 12, -28).lineTo(half - 12, 4).fill({ color: 0x2b1c12 });
  g.moveTo(-half + 12, 4).lineTo(-half + 12, -28).quadraticCurveTo(0, -48, half - 12, -28).lineTo(half - 12, 4).stroke({ color: stoneLo(art), width: 3, alpha: 0.7 });
  g.ellipse(0, 2, half - 18, 11).fill({ color: FLAME_OUT, alpha: 0.5 }); // ember bed glow
  for (const lx of [-18, 2]) g.roundRect(lx, -3, 18, 6, 3).fill(woodDk(art));
  c.addChild(g);
  const f = new Graphics();
  flame(f, -12, -1, 13);
  flame(f, 14, -1, 12);
  flame(f, 1, -6, 18);
  c.addChild(f);
  return c;
}

/** The SMITHY FORGE: a stone forge + glowing coal bed + bellows + chimney hood — the second warm pool.
 *  (The anvil is a separate piece.) Sits against a wall. */
export function forge(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 2, 86, art, 1.05);
  c.addChild(shadow(46, 12, 16));
  const g = new Graphics();
  // stone block
  bodyRect(g, -44, -16, 88, 30, 4, stone(art));
  bodyRect(g, -44, -20, 88, 8, 3, stoneHi(art));
  // hood/chimney
  g.moveTo(-30, -20).lineTo(30, -20).lineTo(18, -58).lineTo(-18, -58).fill(stoneLo(art));
  g.moveTo(-18, -58).lineTo(18, -58).lineTo(14, -72).lineTo(-14, -72).fill(darken(stoneLo(art), 0.1));
  // coal bed (glowing, warm ember — never pure black)
  g.ellipse(0, -16, 26, 9).fill({ color: 0x2b1c12 });
  g.ellipse(0, -16, 22, 7).fill({ color: FLAME_OUT, alpha: 0.75 });
  g.ellipse(0, -17, 13, 4).fill({ color: FLAME_MID, alpha: 0.85 });
  c.addChild(g);
  // 3 staggered flame tongues (tall asymmetric centre) — never two mirrored "eyes"
  const f = new Graphics();
  flame(f, -7, -16, 11);
  flame(f, 9, -16, 13);
  flame(f, 1, -19, 19);
  c.addChild(f);
  // a legible BELLOWS on the left: leather teardrop body + two wooden handles + a nozzle into the fire
  const b = new Graphics();
  b.poly([-46, -4, -70, -12, -75, -1, -70, 10, -46, 6]).fill(mix(0x6a4326, art.wall, 0.1));
  b.poly([-46, -4, -70, -12, -70, -8, -46, -1]).fill({ color: 0xffffff, alpha: 0.12 });
  b.moveTo(-70, -11).lineTo(-84, -15).moveTo(-70, 9).lineTo(-84, 13).stroke({ color: woodDk(art), width: 3 });
  b.rect(-49, -3, 9, 4).fill(iron());
  c.addChild(b);
  return c;
}

/** An ANVIL on a wood stump (Smithy) — the classic iconic silhouette: tapered horn, pinched waist, a
 *  dark-iron body with a lit top face + a little hammer resting on it. Scaled to read at world scale. */
export function anvil(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(22, 7, 20));
  const g = new Graphics();
  bodyRect(g, -13, 6, 26, 16, 2, woodDk(art)); // chunky stump
  const steel = mix(0x4e5660, art.accent, 0.06);
  const steelHi = lighten(steel, 0.3);
  // body: wide top face → pinched waist → flared foot
  g.moveTo(-16, -8).lineTo(18, -8).lineTo(18, -3).lineTo(6, 0).lineTo(6, 6).lineTo(13, 8).lineTo(-13, 8).lineTo(-6, 6).lineTo(-6, 0).lineTo(-16, -3).fill(steel);
  g.poly([18, -8, 30, -6, 30, -3, 18, -3]).fill(steel); // tapered horn
  g.poly([-16, -8, 18, -8, 30, -6, 30, -5, -16, -5]).fill({ color: steelHi, alpha: 0.55 }); // lit top face
  // a small hammer resting on the face
  g.rect(-8, -12, 3, 8).fill(woodDk(art)); // handle
  g.roundRect(-12, -14, 9, 4, 1).fill(darken(steel, 0.1)); // head
  c.addChild(g);
  return c;
}

/** A CAULDRON on a fire tripod (Kitchen cooking-pot / the interactive "warm break"). Steam-ready. */
export function cauldron(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 8, 40, art, 0.85);
  c.addChild(shadow(22, 8, 18));
  const g = new Graphics();
  // tripod legs
  g.moveTo(-16, 16).lineTo(-6, -6).moveTo(16, 16).lineTo(6, -6).moveTo(0, 18).lineTo(0, -6).stroke({ color: iron(), width: 3 });
  // flames under
  c.addChild(g);
  const f0 = new Graphics();
  flame(f0, -4, 6, 8);
  flame(f0, 4, 6, 9);
  c.addChild(f0);
  // pot belly
  const pg = new Graphics();
  bodyEllipse(pg, 0, -10, 20, 16, mix(0x33343c, art.accent, 0.05), true);
  pg.ellipse(0, -20, 17, 6).fill(darken(0x26262c, 0.1));
  pg.ellipse(0, -20, 14, 4.5).fill({ color: mix(0x7a5a2a, art.accent, 0.3), alpha: 0.9 }); // stew
  pg.ellipse(0, -10, 22, 4).stroke({ color: lighten(iron(), 0.2), width: 2, alpha: 0.5 }); // rim band
  c.addChild(pg);
  return c;
}

/** A bubbling STEW / brew puff hint — a couple of soft rising bubbles over a pot (static; the ambient
 *  layer adds live steam over 'coffee'-kind objects). Used as a tiny garnish. */
export function steamHint(): Container {
  const c = new Container();
  c.addChild(
    new Graphics()
      .circle(-3, -8, 4)
      .fill({ color: 0xffffff, alpha: 0.18 })
      .circle(3, -16, 5)
      .fill({ color: 0xffffff, alpha: 0.14 })
      .circle(-1, -24, 4)
      .fill({ color: 0xffffff, alpha: 0.1 }),
  );
  return c;
}

// ── Tables / seating set-dressing ─────────────────────────────────────────────────────────────────

/** A long TRESTLE / FEAST table laden with platters + goblets (the Hall feast tables). `w` is the length. */
export function feastTable(art: ThemeArt, w = 200): Container {
  const c = new Container();
  const half = w / 2;
  c.addChild(shadow(half + 6, 14, 12));
  const g = new Graphics();
  // trestle legs
  g.moveTo(-half + 18, 6).lineTo(-half + 28, 22).moveTo(-half + 28, 6).lineTo(-half + 18, 22)
    .moveTo(half - 18, 6).lineTo(half - 28, 22).moveTo(half - 28, 6).lineTo(half - 18, 22).stroke({ color: woodDk(art), width: 4 });
  // top
  bodyRect(g, -half, -10, w, 16, 3, wood(art));
  g.rect(-half, -10, w, 4).fill({ color: 0xffffff, alpha: 0.12 });
  // a runner cloth
  g.rect(-half + 16, -8, w - 32, 8).fill({ color: mix(0x8a2f2f, art.accent, 0.1), alpha: 0.55 });
  c.addChild(g);
  // platters + goblets along it
  const food = new Graphics();
  for (let x = -half + 30; x < half - 20; x += 46) {
    food.ellipse(x, -6, 9, 4).fill({ color: lighten(wood(art), 0.2) }); // platter
    food.ellipse(x, -7, 6, 2.6).fill({ color: mix(0x9a6a3a, art.accent, 0.2) }); // roast/bread
    food.roundRect(x + 16, -10, 4, 7, 1).fill({ color: gold(art), alpha: 0.85 }); // goblet
  }
  c.addChild(food);
  return c;
}

/** A grand round COUNCIL TABLE with a painted realm-map surface + carved markers (War Room). */
export function roundTable(art: ThemeArt, r = 64): Container {
  const c = new Container();
  c.addChild(shadow(r + 6, (r + 6) * 0.5, 8));
  const g = new Graphics();
  g.ellipse(0, 8, r, r * 0.52).fill(woodDk(art)); // edge
  bodyEllipse(g, 0, 0, r, r * 0.55, wood(art));
  // map surface
  g.ellipse(0, -2, r - 12, (r - 12) * 0.55).fill({ color: mix(0x9a8a5a, art.wall, 0.2), alpha: 0.85 });
  g.moveTo(-r / 2, -6).bezierCurveTo(-10, 0, 10, 4, r / 2, -2).stroke({ color: 0x3a6ac2, width: 2, alpha: 0.6 }); // river
  c.addChild(g);
  // carved unit markers
  const m = new Graphics();
  for (const [mx, my, col] of [[-20, -6, 0xc24a3a], [10, 2, 0x3a6ac2], [-4, -10, 0x4aa05a]] as const) {
    m.circle(mx, my, 3.5).fill({ color: col, alpha: 0.95 });
    m.rect(mx - 1, my - 8, 2, 6).fill({ color: col, alpha: 0.95 });
  }
  c.addChild(m);
  return c;
}

/** A slanted SCRIBE DESK with quill, inkwell, illuminated page + its own candle glow (Scriptorium). */
export function scribeDesk(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 0, 24, art, 0.5);
  c.addChild(shadow(20, 7, 14));
  const g = new Graphics();
  // legs + slanted top
  g.rect(-16, 2, 4, 14).fill(woodDk(art)).rect(12, 2, 4, 14).fill(woodDk(art));
  g.moveTo(-18, 2).lineTo(18, 2).lineTo(14, -16).lineTo(-22, -16).fill(wood(art));
  g.moveTo(-22, -16).lineTo(14, -16).lineTo(14, -13).lineTo(-22, -13).fill({ color: 0xffffff, alpha: 0.12 });
  // parchment page on the slope
  g.moveTo(-14, -4).lineTo(8, -4).lineTo(6, -14).lineTo(-16, -14).fill(0xf2ead2);
  g.moveTo(-13, -7).lineTo(2, -7).moveTo(-13, -10).lineTo(4, -10).stroke({ color: 0x6a5a3a, width: 1, alpha: 0.6 });
  g.rect(-15, -13, 3, 3).fill({ color: gold(art), alpha: 0.8 }); // illuminated capital
  c.addChild(g);
  // candle + quill + inkwell
  const f = new Graphics();
  f.roundRect(14, -18, 3, 8, 1).fill(0xf3e6c0);
  flame(f, 15.5, -18, 4);
  f.circle(-18, -2, 2.4).fill(0x1a1a22); // inkwell
  f.moveTo(-18, -4).lineTo(-22, -14).stroke({ color: 0xeeeeee, width: 1.5 }); // quill
  c.addChild(f);
  return c;
}

/** A tall arched BOOKCASE of coloured leather spines (some softly glowing — the Library's interactive
 *  tell). `h` tall. */
export function bookcase(art: ThemeArt, h = 130): Container {
  const c = new Container();
  c.addChild(shadow(34, 7, h / 2 + 4));
  const g = new Graphics();
  const w = 64;
  // arched frame
  g.moveTo(-w / 2, h / 2).lineTo(-w / 2, -h / 2 + 14).quadraticCurveTo(0, -h / 2 - 8, w / 2, -h / 2 + 14).lineTo(w / 2, h / 2).fill(wood(art));
  g.moveTo(-w / 2, h / 2).lineTo(-w / 2, -h / 2 + 14).quadraticCurveTo(0, -h / 2 - 8, w / 2, -h / 2 + 14).lineTo(w / 2 - 5, -h / 2 + 16).quadraticCurveTo(0, -h / 2 + 2, -w / 2 + 5, -h / 2 + 16).fill({ color: 0xffffff, alpha: 0.08 });
  // shelves of spines
  const spines = [0xc24a3a, 0xe0a93a, 0x3a6ac2, 0x4aa05a, 0x7a4aa0, 0xb08a4a];
  const rows = Math.floor((h - 30) / 26);
  for (let r = 0; r < rows; r++) {
    const y = -h / 2 + 26 + r * 26;
    g.rect(-w / 2 + 6, y + 12, w - 12, 3).fill(woodDk(art)); // shelf board
    for (let i = 0; i < 9; i++) {
      const bh = 16 + (i % 3) * 3;
      const col = spines[(r * 9 + i) % spines.length]!;
      g.rect(-w / 2 + 8 + i * 6, y + 11 - bh, 5, bh).fill(col);
      if ((r + i) % 7 === 0) g.rect(-w / 2 + 8 + i * 6, y + 11 - bh, 5, 2).fill({ color: lighten(art.glow, 0.2), alpha: 0.8 }); // a glowing tome
    }
  }
  c.addChild(g);
  return c;
}

/** Pigeonhole SCROLL SHELVES — rolled parchment in cubbies (Scriptorium/Library). */
export function scrollShelf(art: ThemeArt, w = 70, h = 80): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 6, h / 2 + 2));
  const g = new Graphics();
  bodyRect(g, -w / 2, -h / 2, w, h, 3, wood(art));
  for (let r = 0; r < 3; r++) {
    for (let q = 0; q < 4; q++) {
      const x = -w / 2 + 8 + q * ((w - 16) / 4);
      const y = -h / 2 + 8 + r * ((h - 16) / 3);
      g.roundRect(x, y, (w - 16) / 4 - 4, (h - 16) / 3 - 4, 2).fill(darken(wood(art), 0.18));
      g.circle(x + ((w - 16) / 4 - 4) / 2, y + ((h - 16) / 3 - 4) / 2, 3).fill({ color: 0xf2ead2, alpha: 0.9 }); // scroll end
    }
  }
  c.addChild(g);
  return c;
}

/** A standing LECTERN with an open book (scribe-lectern / herald's scroll / guest ledger). */
export function lectern(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(12, 5, 18));
  const g = new Graphics();
  g.rect(-2, -2, 4, 20).fill(woodDk(art)); // post
  g.ellipse(0, 18, 9, 3).fill(woodDk(art)); // base
  g.moveTo(-14, -8).lineTo(14, -8).lineTo(12, -2).lineTo(-12, -2).fill(wood(art)); // slanted top
  // open book
  g.moveTo(-13, -9).lineTo(0, -7).lineTo(0, -2).lineTo(-12, -3).fill(0xf2ead2);
  g.moveTo(13, -9).lineTo(0, -7).lineTo(0, -2).lineTo(12, -3).fill(0xeee2c8);
  g.moveTo(-10, -7).lineTo(-2, -6).moveTo(2, -6).lineTo(10, -7).stroke({ color: 0x6a5a3a, width: 0.8, alpha: 0.6 });
  c.addChild(g);
  return c;
}

/** A stone ALTAR with a draped cloth + golden chalice (Chapel). */
export function altar(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(34, 9, 16));
  const g = new Graphics();
  bodyRect(g, -32, -6, 64, 22, 3, stone(art));
  g.rect(-34, -10, 68, 8).fill(stoneHi(art)); // top slab
  g.rect(-30, -2, 60, 14).fill({ color: mix(0x8a2f2f, art.accent, 0.1), alpha: 0.7 }); // cloth
  g.rect(-30, 8, 60, 4).fill({ color: gold(art), alpha: 0.7 }); // trim
  c.addChild(g);
  // chalice + candles
  const f = new Graphics();
  f.moveTo(-4, -10).lineTo(4, -10).lineTo(2, -18).lineTo(-2, -18).fill(gold(art));
  f.ellipse(0, -19, 5, 2).fill(gold(art));
  for (const cx of [-22, 22]) {
    f.roundRect(cx - 1.5, -20, 3, 10, 1).fill(0xf3e6c0);
    flame(f, cx, -20, 4);
  }
  c.addChild(f);
  glowPool(c, 0, -2, 36, art, 0.55);
  return c;
}

/** A short wooden PEW / prayer bench (Chapel) / arrival bench (Gatehouse). `w` length. */
export function pew(art: ThemeArt, w = 86): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 7, 12));
  const g = new Graphics();
  const half = w / 2;
  g.rect(-half + 6, 2, 5, 14).fill(woodDk(art)).rect(half - 11, 2, 5, 14).fill(woodDk(art)); // legs
  bodyRect(g, -half, -6, w, 10, 3, wood(art)); // seat
  bodyRect(g, -half, -22, w, 8, 3, darken(wood(art), 0.08)); // back
  for (let x = -half + 10; x < half - 6; x += 18) g.rect(x, -20, 3, 14).fill({ color: woodDk(art), alpha: 0.5 }); // slats
  c.addChild(g);
  return c;
}

/** A simple round wooden STOOL (no back) — smithy/kitchen/tavern gather seating. Place at a seat anchor. */
export function woodStool(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(9, 3, 8));
  const g = new Graphics();
  g.moveTo(-6, 6).lineTo(-8, 14).moveTo(6, 6).lineTo(8, 14).moveTo(0, 7).lineTo(0, 15).stroke({ color: woodDk(art), width: 2 });
  bodyEllipse(g, 0, 2, 9, 5, wood(art));
  c.addChild(g);
  return c;
}

/** A wooden CHAIR with a back — council / reading / dining seating. `facing` (radians) is the way the
 *  sitter looks; the backrest sits on the OPPOSITE side. Place at a seat anchor with its matching facing. */
export function woodChair(art: ThemeArt, facing = -Math.PI / 2): Container {
  void facing; // a tall back-at-top silhouette reads cleanly at world scale regardless of facing direction
  const c = new Container();
  c.addChild(shadow(13, 5, 12));
  const w = wood(art);
  const g = new Graphics();
  // four splayed legs
  g.moveTo(-8, 8).lineTo(-10, 18).moveTo(8, 8).lineTo(10, 18).moveTo(-5, 9).lineTo(-6, 19).moveTo(5, 9).lineTo(6, 19).stroke({ color: woodDk(art), width: 2.4 });
  // a TALL HIGH BACK rising above the seat (the clear "high-back chair" silhouette) + a carved crest finial
  g.roundRect(-11, -23, 22, 27, 4).fill(darken(w, 0.1));
  g.roundRect(-11, -23, 22, 7, 4).fill({ color: 0xffffff, alpha: 0.14 }); // lit top
  g.roundRect(-8, -16, 16, 4, 2).fill({ color: art.accent, alpha: 0.3 }); // carved rail
  g.roundRect(-5, -28, 10, 7, 2).fill(w); // finial crest
  // seat cushion in FRONT (lower), overlapping the back's base for depth
  bodyRect(g, -12, 0, 24, 13, 4, w);
  c.addChild(g);
  return c;
}

// ── Tavern / cellar / barracks ─────────────────────────────────────────────────────────────────────

/** The TAVERN ALE-BAR — a long wooden bar with a tapped keg + tankards. `w` length. */
export function aleBar(art: ThemeArt, w = 150): Container {
  const c = new Container();
  const half = w / 2;
  c.addChild(shadow(half, 12, 14));
  const g = new Graphics();
  bodyRect(g, -half, -8, w, 20, 3, wood(art)); // bar top
  bodyRect(g, -half + 4, 12, w - 8, 16, 2, woodDk(art)); // front panel
  g.rect(-half, -8, w, 4).fill({ color: 0xffffff, alpha: 0.12 });
  c.addChild(g);
  // a keg on the bar + tankards
  const k = new Graphics();
  bodyEllipse(k, half - 28, -18, 14, 12, mix(0x7a5126, art.wall, 0.1));
  k.rect(half - 42, -22, 28, 3).fill({ color: gold(art), alpha: 0.6 }).rect(half - 42, -14, 28, 3).fill({ color: gold(art), alpha: 0.6 });
  k.rect(half - 30, -8, 4, 4).fill(iron()); // tap
  for (const tx of [-half + 16, -half + 34, -8]) {
    k.roundRect(tx, -16, 7, 9, 1.5).fill(mix(0x9aa3b0, art.accent, 0.1));
    k.ellipse(tx + 3.5, -16, 4, 1.6).fill({ color: 0xf0e6c0, alpha: 0.8 }); // froth
  }
  c.addChild(k);
  return c;
}

/** A wooden BARREL (kitchen ale / cellar). Single. */
export function barrel(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(13, 5, 16));
  const g = new Graphics();
  bodyRect(g, -12, -14, 24, 30, 8, mix(0x7a5126, art.wall, 0.12));
  g.rect(-12, -8, 24, 3).fill({ color: gold(art), alpha: 0.5 }).rect(-12, 4, 24, 3).fill({ color: gold(art), alpha: 0.5 });
  for (const sx of [-6, 0, 6]) g.rect(sx - 1, -14, 2, 30).fill({ color: darken(0x7a5126, 0.2), alpha: 0.4 }); // staves
  c.addChild(g);
  return c;
}

/** A stack of BARRELS (3) — cellar / kitchen storage. */
export function barrelStack(art: ThemeArt): Container {
  const c = new Container();
  const a = barrel(art); a.x = -13; a.y = 6;
  const b = barrel(art); b.x = 13; b.y = 6;
  const t = barrel(art); t.x = 0; t.y = -18; t.scale.set(0.9);
  c.addChild(a, b, t);
  return c;
}

/** A diagonal WINE RACK of bottles (Cellar). */
export function wineRack(art: ThemeArt, w = 70, h = 80): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 6, h / 2 + 2));
  const g = new Graphics();
  bodyRect(g, -w / 2, -h / 2, w, h, 3, woodDk(art));
  // diamond lattice
  for (let i = -2; i <= 4; i++) {
    g.moveTo(-w / 2, -h / 2 + i * 22).lineTo(-w / 2 + h, -h / 2 + i * 22 - h).stroke({ color: wood(art), width: 2, alpha: 0.5 });
    g.moveTo(-w / 2, -h / 2 + i * 22 - h).lineTo(-w / 2 + h, -h / 2 + i * 22).stroke({ color: wood(art), width: 2, alpha: 0.5 });
  }
  // bottle ends
  for (let r = 0; r < 3; r++) for (let q = 0; q < 4; q++) {
    const x = -w / 2 + 12 + q * ((w - 20) / 3);
    const y = -h / 2 + 14 + r * ((h - 24) / 2.4);
    g.circle(x, y, 4).fill({ color: [0x3a5a2a, 0x5a2a2a, 0x2a3a4a][(r + q) % 3]!, alpha: 0.9 });
    g.circle(x - 1, y - 1, 1.4).fill({ color: 0xffffff, alpha: 0.3 });
  }
  c.addChild(g);
  return c;
}

/** An aging CASK on a stand, crest-stamped (Cellar showpiece / reserve). */
export function cask(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(24, 8, 18));
  const g = new Graphics();
  g.moveTo(-22, 16).lineTo(-14, 4).moveTo(22, 16).lineTo(14, 4).stroke({ color: woodDk(art), width: 4 }); // stand
  bodyEllipse(g, 0, -6, 26, 18, mix(0x7a5126, art.wall, 0.12));
  g.ellipse(0, -6, 11, 14).fill(darken(0x6a4322, 0.05)); // end face
  g.ellipse(0, -6, 11, 14).stroke({ color: gold(art), width: 2, alpha: 0.5 });
  g.circle(0, -6, 6).fill({ color: gold(art), alpha: 0.5 }); // crest stamp
  g.rect(-26, -12, 52, 3).fill({ color: gold(art), alpha: 0.5 }).rect(-26, 0, 52, 3).fill({ color: gold(art), alpha: 0.5 });
  g.rect(-2, 6, 4, 4).fill(iron()); // tap
  c.addChild(g);
  return c;
}

/** A small candlelit TASTING / READING table — a solid warm-oak pedestal table (Cellar / Library / Tavern).
 *  Reads as a real table, never a dark plank on spidery legs. */
export function smallTable(art: ThemeArt, r = 34): Container {
  const c = new Container();
  c.addChild(shadow(r, r * 0.5, 12));
  const g = new Graphics();
  // a sturdy central pedestal + foot (solid, grounded)
  g.roundRect(-5, 2, 10, 15, 2).fill(woodDk(art));
  g.ellipse(0, 18, 13, 4).fill(woodDk(art));
  // round top: edge thickness + a clear lit oak surface
  g.ellipse(0, 4, r, r * 0.55).fill(darken(wood(art), 0.2));
  bodyEllipse(g, 0, 0, r, r * 0.55, wood(art));
  c.addChild(g);
  const f = new Graphics();
  f.roundRect(-1.5, -12, 3, 8, 1).fill(0xf3e6c0); flame(f, 0, -12, 5); // a warm candle
  for (const gx of [-15, 11]) f.roundRect(gx, -8, 6, 8, 1).fill({ color: gold(art), alpha: 0.85 }); // goblets
  c.addChild(f);
  glowPool(c, 0, -2, 26, art, 0.55);
  return c;
}

/** A potted LILY arrangement — pale stems + cream/white blooms in a slim urn (Chapel altar flowers).
 *  Reads as ceremonial flowers, NOT a green garden hedge. */
export function pottedLily(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(9, 4, 14));
  const g = new Graphics();
  // slim pale-stone urn
  g.poly([-8, 2, 8, 2, 6, 15, -6, 15]).fill(mix(0xb9b2a0, art.wall, 0.2));
  g.roundRect(-9, -1, 18, 4, 1.5).fill(lighten(mix(0xb9b2a0, art.wall, 0.2), 0.12));
  // a few thin pale stems (kept minimal so the WHITE blooms dominate, never a green bush)
  const stem = mix(0x7a9a5a, art.wall, 0.1);
  for (const [sx, h] of [[-6, 22], [0, 28], [6, 20]] as const) g.moveTo(sx, 2).lineTo(sx * 1.5, 4 - h).stroke({ color: stem, width: 1.5 });
  // big cream/white trumpet-lily blooms (the dominant read) with a soft gold heart + a pale halo
  for (const [bx, by] of [[-9, -22], [0, -30], [9, -20], [-3, -14], [5, -12]] as const) {
    g.circle(bx, by, 3).fill({ color: 0xffffff, alpha: 0.16 }); // halo
    g.poly([bx, by - 8, bx + 7, by, bx, by + 6, bx - 7, by]).fill(0xf6f1e4); // bloom petals
    g.circle(bx, by, 2.4).fill({ color: gold(art), alpha: 0.7 });
  }
  c.addChild(g);
  return c;
}

/** A round DICING / CARDS table with dice + cards (Barracks). */
export function diceTable(art: ThemeArt, r = 40): Container {
  const c = new Container();
  c.addChild(shadow(r, r * 0.5, 8));
  const g = new Graphics();
  g.moveTo(-r + 8, 4).lineTo(-r + 4, 18).moveTo(r - 8, 4).lineTo(r - 4, 18).stroke({ color: woodDk(art), width: 3 });
  bodyEllipse(g, 0, 0, r, r * 0.55, wood(art));
  g.ellipse(0, -1, r - 8, (r - 8) * 0.55).fill({ color: mix(0x3a5a3a, art.wall, 0.2), alpha: 0.7 }); // felt
  c.addChild(g);
  const d = new Graphics();
  for (const [dx, dy] of [[-8, -2], [-2, 3]] as const) {
    d.roundRect(dx, dy, 7, 7, 1.5).fill(0xf2ead2);
    d.circle(dx + 2, dy + 2, 0.9).fill(0x1a1a22).circle(dx + 5, dy + 5, 0.9).fill(0x1a1a22);
  }
  d.roundRect(8, -4, 8, 11, 1).fill(0xf6f0e0).roundRect(11, -2, 8, 11, 1).fill(0xfaf4e6); // cards
  c.addChild(d);
  return c;
}

/** A BUNK BED (two tiers) — Barracks. A warm wooden frame with headboards, cream linen + a pillow lump,
 *  so it reads as a real bed (never flat grey/red "progress bars"). */
export function bunkBed(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(32, 8, 28));
  const g = new Graphics();
  const fr = wood(art);
  // sturdy wooden corner posts
  for (const px of [-30, 30]) {
    g.roundRect(px - 3, -36, 6, 64, 2).fill(fr);
    g.roundRect(px - 3, -36, 6, 6, 2).fill({ color: 0xffffff, alpha: 0.12 });
  }
  // two bunks: wood headboard plank + a cream linen mattress + a pillow + a folded blanket
  for (const my of [0, -28]) {
    g.roundRect(-30, my - 6, 60, 6, 2).fill(woodDk(art)); // headboard rail
    bodyRect(g, -28, my, 56, 11, 3, 0xe7ddc4); // cream linen mattress
    g.roundRect(-26, my + 1, 15, 9, 3).fill({ color: 0xf6efdc }); // pillow
    g.roundRect(2, my + 1, 24, 9, 3).fill({ color: mix(0x8a3a3a, art.accent, 0.08), alpha: 0.9 }); // folded blanket
  }
  c.addChild(g);
  return c;
}

// ── Treasure / discovery ───────────────────────────────────────────────────────────────────────────

/** A glowing RELIC PEDESTAL — a velvet/stone plinth holding a softly glowing relic under a beam (the
 *  Vault discovery showpiece). Static accent glow (the pulse can be added by ambient later). */
export function relicPedestal(art: ThemeArt): Container {
  const c = new Container();
  // the single brightest pool in the Vault — a wide halo + a hot core under the relic
  glowPool(c, 0, -28, 70, art, 1.3);
  glowPool(c, 0, -34, 34, art, 1.1);
  c.addChild(shadow(22, 8, 20));
  const g = new Graphics();
  bodyRect(g, -17, 4, 34, 18, 2, stoneLo(art)); // base
  bodyRect(g, -11, -12, 22, 16, 2, stone(art)); // column
  bodyRect(g, -19, -18, 38, 9, 2, stoneHi(art)); // top slab
  g.rect(-19, -18, 38, 2).fill({ color: gold(art), alpha: 0.6 }); // gold rim
  c.addChild(g);
  // the relic — a big faceted glowing gem with a bright core, sparkle + a beam halo
  const r = new Graphics();
  r.circle(0, -36, 18).fill({ color: art.glow, alpha: 0.28 }); // bloom
  r.poly([0, -56, 13, -38, 0, -22, -13, -38]).fill({ color: lighten(art.accent, 0.35), alpha: 0.97 }); // gem
  r.poly([0, -56, 13, -38, 0, -38]).fill({ color: 0xffffff, alpha: 0.4 }); // lit facet
  r.circle(0, -39, 4).fill({ color: 0xffffff, alpha: 0.85 }); // hot core
  // sparkle
  r.moveTo(0, -62).lineTo(0, -50).moveTo(-7, -56).lineTo(7, -56).stroke({ color: 0xfff3c4, width: 1.5, alpha: 0.7 });
  c.addChild(r);
  return c;
}

/** A TREASURE PILE — an open iron-bound chest overflowing with gold coins. */
export function treasurePile(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(28, 9, 14));
  const g = new Graphics();
  // chest
  bodyRect(g, -22, -2, 44, 18, 3, woodDk(art));
  g.rect(-22, 2, 44, 3).fill({ color: gold(art), alpha: 0.6 });
  // open lid
  g.moveTo(-22, -2).lineTo(22, -2).lineTo(18, -18).lineTo(-18, -18).fill(darken(woodDk(art), 0.1));
  g.moveTo(-18, -18).lineTo(18, -18).lineTo(18, -15).lineTo(-18, -15).fill({ color: gold(art), alpha: 0.5 });
  c.addChild(g);
  // coin spill
  const coins = new Graphics();
  for (let i = 0; i < 22; i++) {
    const cx = -20 + (i * 137.5) % 40;
    const cy = -4 + ((i * 53) % 14);
    coins.ellipse(cx, cy, 3.4, 2).fill({ color: gold(art), alpha: 0.95 });
    coins.ellipse(cx - 0.6, cy - 0.5, 1.4, 0.9).fill({ color: 0xfff0b0, alpha: 0.7 });
  }
  c.addChild(coins);
  glowPool(c, 0, 0, 30, art, 0.45);
  return c;
}

/** A jewel DISPLAY PLINTH under a beam (crown/goblet) — Vault. */
export function gemPlinth(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(12, 5, 16));
  const g = new Graphics();
  bodyRect(g, -10, -2, 20, 18, 2, mix(0x5a4a6a, art.wall, 0.2)); // velvet plinth
  g.rect(-10, -2, 20, 3).fill({ color: gold(art), alpha: 0.6 });
  // a crown
  g.moveTo(-8, -8).lineTo(-8, -16).lineTo(-4, -10).lineTo(0, -18).lineTo(4, -10).lineTo(8, -16).lineTo(8, -8).fill(gold(art));
  for (const cx of [-8, 0, 8]) g.circle(cx, -16, 1.6).fill({ color: 0xc23a8a, alpha: 0.95 });
  c.addChild(g);
  glowPool(c, 0, -6, 22, art, 0.4);
  return c;
}

/** Twin gem-eyed DRAGON STATUE guardian (flanks the Vault door / ceremonial sentinel). */
export function dragonStatue(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 22));
  const g = new Graphics();
  const st = stone(art);
  // coiled body
  bodyRect(g, -8, 4, 16, 18, 4, st); // plinth/base
  g.moveTo(-6, 4).quadraticCurveTo(-16, -10, -2, -18).quadraticCurveTo(10, -24, 6, -10).quadraticCurveTo(2, -2, -2, 2).fill(st); // neck/serpent
  g.ellipse(2, -20, 7, 6).fill(st); // head
  g.poly([8, -24, 14, -26, 9, -20].map((v) => v)).fill(stoneLo(art)); // snout
  g.poly([-2, -26, 0, -30, 3, -26].map((v) => v)).fill(stoneLo(art)); // horn
  g.circle(5, -21, 1.6).fill({ color: 0x4ad0a0, alpha: 0.95 }); // gem eye
  g.moveTo(-6, 4).quadraticCurveTo(-16, -10, -2, -18).stroke({ color: 0xffffff, alpha: 0.08, width: 4 });
  c.addChild(g);
  return c;
}

/** A giant set of GOLD WEIGHING SCALES (Vault). */
export function goldScales(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(20, 6, 18));
  const g = new Graphics();
  g.rect(-2, -28, 4, 46).fill(iron()); // post
  g.ellipse(0, 18, 12, 4).fill(iron()); // base
  g.moveTo(-20, -26).lineTo(20, -26).stroke({ color: iron(), width: 3 }); // beam
  for (const sx of [-20, 20]) {
    g.moveTo(sx, -26).lineTo(sx - 6, -14).moveTo(sx, -26).lineTo(sx + 6, -14).stroke({ color: iron(), width: 1.5 });
    g.ellipse(sx, -12, 9, 4).fill(mix(0x8a8a96, art.accent, 0.1));
    g.ellipse(sx, -13, 6, 2).fill({ color: gold(art), alpha: 0.9 });
  }
  c.addChild(g);
  return c;
}

// ── Stages / ambient life ──────────────────────────────────────────────────────────────────────────

/** The BARD'S STAGE — a small raised corner dais with a lute + drum (the Tavern jukebox/disco object). */
export function bardStage(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 2, 40, art, 0.6);
  c.addChild(shadow(30, 10, 14));
  const g = new Graphics();
  bodyRect(g, -30, -8, 60, 18, 3, wood(art)); // dais
  g.rect(-30, -8, 60, 4).fill({ color: 0xffffff, alpha: 0.1 });
  g.rect(-30, 8, 60, 4).fill(woodDk(art));
  c.addChild(g);
  // a lute leaning
  const l = new Graphics();
  l.ellipse(-12, -20, 8, 11).fill(mix(0x8a5a2a, art.wall, 0.1));
  l.ellipse(-12, -20, 8, 11).stroke({ color: woodDk(art), width: 1.5 });
  l.circle(-12, -20, 3).fill(0x1a1208);
  l.rect(-11, -42, 3, 24).fill(woodDk(art));
  for (const sx of [-13, -11.5, -10] as const) l.moveTo(sx, -30).lineTo(sx, -10).stroke({ color: 0xeeeeee, width: 0.5, alpha: 0.6 });
  c.addChild(l);
  // a drum
  const d = new Graphics();
  bodyEllipse(d, 14, -10, 13, 9, mix(0x8a3a2a, art.accent, 0.1));
  d.ellipse(14, -14, 11, 6).fill(0xf2ead2);
  d.rect(2, -16, 24, 3).fill({ color: gold(art), alpha: 0.6 }).rect(2, -8, 24, 3).fill({ color: gold(art), alpha: 0.6 });
  c.addChild(d);
  // floating music notes
  c.addChild(new Graphics().circle(24, -34, 2.4).fill(art.accent).rect(25.6, -42, 1.4, 8).fill(art.accent).circle(30, -38, 2).fill(lighten(art.accent, 0.2)).rect(31.4, -45, 1.2, 7).fill(lighten(art.accent, 0.2)));
  return c;
}

/** A RAVEN on a perch (War Room message-bird / notifications motif). */
export function raven(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(8, 3, 18));
  const g = new Graphics();
  g.rect(-1.5, -2, 3, 20).fill(woodDk(art)); // perch post
  g.moveTo(-10, -2).lineTo(10, -2).stroke({ color: woodDk(art), width: 3 }); // crossbar
  // bird body
  g.ellipse(0, -10, 6, 8).fill(0x1c1c24);
  g.ellipse(0, -16, 4, 4.5).fill(0x1c1c24); // head
  g.poly([3, -17, 9, -16, 3, -14].map((v) => v)).fill(0x3a3320); // beak
  g.poly([-2, -4, -8, 4, 0, -2].map((v) => v)).fill(0x14141a); // tail
  g.circle(1.5, -16.5, 0.9).fill({ color: gold(art), alpha: 0.9 }); // eye glint
  c.addChild(g);
  return c;
}

/** A sleeping castle HOUND curled on a cushion (Barracks ambient life). */
export function houndAsleep(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(20, 7, 6));
  const g = new Graphics();
  g.ellipse(0, 2, 20, 11).fill({ color: mix(0x8a2f3f, art.accent, 0.1), alpha: 0.7 }); // cushion
  const fur = mix(0x6a5340, art.wall, 0.1);
  g.ellipse(0, -2, 16, 9).fill(fur); // curled body
  g.ellipse(-12, -4, 7, 6).fill(fur); // head tucked
  g.poly([-18, -6, -12, -10, -14, -2].map((v) => v)).fill(darken(fur, 0.12)); // ear
  g.ellipse(12, -2, 6, 4).fill(darken(fur, 0.08)); // tail over nose
  // little zzz
  c.addChild(new Graphics().moveTo(-16, -14).lineTo(-12, -14).lineTo(-16, -10).lineTo(-12, -10).stroke({ color: 0xffffff, width: 1, alpha: 0.4 }));
  c.addChild(g);
  return c;
}

/** A simple stone STATUE plinth (generic ceremonial sculpture). */
export function statue(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 24));
  const g = new Graphics();
  bodyRect(g, -14, 12, 28, 12, 2, stoneLo(art)); // plinth
  bodyRect(g, -16, 6, 32, 8, 2, stone(art));
  // a robed figure
  g.moveTo(-8, 8).quadraticCurveTo(-10, -20, 0, -28).quadraticCurveTo(10, -20, 8, 8).fill(stoneHi(art));
  g.ellipse(0, -30, 6, 7).fill(stone(art)); // head
  g.moveTo(-8, 8).quadraticCurveTo(-9, -14, -2, -24).stroke({ color: 0xffffff, alpha: 0.1, width: 3 });
  c.addChild(g);
  return c;
}

// ── The Throne (the communal "Seat of the Guild") ──────────────────────────────────────────────────

/** THE COMMUNAL THRONE — a high-backed carved "Seat of the Guild" with a stone canopy arch + a soft halo.
 *  Read as elevated via a step-shadow band (NOT real height). Deliberately warm + welcoming, never a
 *  watch-post. Draw it with a small accent glow behind. */
export function throne(art: ThemeArt): Container {
  const c = new Container();
  // soft accent halo behind (less intense than the hearth)
  c.addChild(new Graphics().ellipse(0, -30, 46, 56).fill({ color: art.glow, alpha: 0.1 * art.glowStrength }));
  c.addChild(shadow(34, 11, 24));
  const g = new Graphics();
  // dais step shadow band (reads as raised)
  g.ellipse(0, 26, 52, 14).fill({ color: 0x000000, alpha: 0.12 });
  // stone canopy arch behind the throne
  g.moveTo(-34, 20).lineTo(-34, -52).quadraticCurveTo(0, -86, 34, -52).lineTo(34, 20).lineTo(26, 20).lineTo(26, -48).quadraticCurveTo(0, -74, -26, -48).lineTo(-26, 20).fill(stone(art));
  g.moveTo(-34, -52).quadraticCurveTo(0, -86, 34, -52).quadraticCurveTo(0, -78, -34, -52).fill({ color: 0xffffff, alpha: 0.07 });
  c.addChild(g);
  // the throne chair (carved oak + gold + red cushion)
  const t = new Graphics();
  bodyRect(t, -20, -2, 40, 18, 3, woodDk(art)); // seat block
  t.roundRect(-17, -4, 34, 8, 3).fill({ color: mix(0xc23a3a, art.accent, 0.1), alpha: 0.95 }); // cushion
  // tall back
  t.moveTo(-18, -2).lineTo(-18, -46).quadraticCurveTo(0, -56, 18, -46).lineTo(18, -2).fill(wood(art));
  t.moveTo(-18, -2).lineTo(-18, -46).quadraticCurveTo(0, -56, 18, -46).lineTo(13, -44).quadraticCurveTo(0, -50, -13, -44).lineTo(-13, -2).fill({ color: mix(0x8a2f2f, art.accent, 0.08), alpha: 0.9 }); // back cushion
  // armrests
  bodyRect(t, -24, -6, 7, 22, 3, woodDk(art));
  bodyRect(t, 17, -6, 7, 22, 3, woodDk(art));
  // gold finials + trim
  t.circle(-21, -8, 3.5).fill(gold(art)).circle(21, -8, 3.5).fill(gold(art));
  t.moveTo(-16, -44).quadraticCurveTo(0, -53, 16, -44).stroke({ color: gold(art), width: 2.5, alpha: 0.9 });
  t.poly([0, -56, 5, -48, 0, -44, -5, -48].map((v) => v)).fill(gold(art)); // crown finial
  c.addChild(t);
  return c;
}

/** The PORTCULLIS ARCH backdrop — a heavy stone gate arch + a chain-winch (the controllable gate gap is
 *  drawn by the door system; this is the surrounding stonework + a hanging bell-rope). Hang at the gate. */
export function portcullisArch(art: ThemeArt): Container {
  const c = new Container();
  const g = new Graphics();
  // chunky stone arch
  g.moveTo(-44, 70).lineTo(-44, -30).quadraticCurveTo(0, -70, 44, -30).lineTo(44, 70).lineTo(28, 70).lineTo(28, -24).quadraticCurveTo(0, -54, -28, -24).lineTo(-28, 70).fill(stone(art));
  g.moveTo(-44, -30).quadraticCurveTo(0, -70, 44, -30).quadraticCurveTo(0, -62, -44, -30).fill({ color: 0xffffff, alpha: 0.07 });
  // crenellation hint atop
  for (const bx of [-44, -28, -12, 4, 20, 36]) g.rect(bx, -36, 10, 10).fill(stoneLo(art));
  c.addChild(g);
  // the iron PORTCULLIS grille, raised + hanging under the arch (the gate is OPEN by default): a clear
  // lattice of vertical bars with pointed tooth tips + two cross-rails, so it unmistakably reads as a gate.
  const bars = new Graphics();
  for (let tx = -26; tx <= 26; tx += 8.5) {
    bars.rect(tx - 1.6, -54, 3.2, 26).fill({ color: iron(), alpha: 0.9 });
    bars.poly([tx - 1.6, -28, tx + 1.6, -28, tx, -22]).fill({ color: iron(), alpha: 0.9 }); // tooth tip
  }
  bars.rect(-28, -52, 56, 3).fill({ color: lighten(iron(), 0.25), alpha: 0.8 });
  bars.rect(-28, -36, 56, 3).fill({ color: iron(), alpha: 0.85 });
  c.addChild(bars);
  // chain-winch + bell-rope to the side
  c.addChild(new Graphics().circle(54, -10, 8).fill(woodDk(art)).circle(54, -10, 3).fill(iron()).moveTo(54, -2).lineTo(54, 40).stroke({ color: 0x8a7a4a, width: 2 }));
  return c;
}

/** The royal CRIMSON-AND-GOLD ceremonial RUNNER rug — the Hall's central navigation spine (the classic
 *  warm throne-room red carpet, harmonising with the honey-stone palette). `w`×`h`; lay it FIRST (decal). */
export function runnerRug(art: ThemeArt, w: number, h: number): Container {
  const c = new Container();
  const crimson = mix(0x7a2433, art.accent, 0.12);
  const g = new Graphics()
    .roundRect(-w / 2, -h / 2, w, h, 8)
    .fill({ color: crimson, alpha: 0.66 })
    .roundRect(-w / 2 + 8, -h / 2 + 8, w - 16, h - 16, 6)
    .stroke({ color: gold(art), width: 2.5, alpha: 0.6 })
    .roundRect(-w / 2 + 14, -h / 2 + 14, w - 28, h - 28, 4)
    .stroke({ color: gold(art), width: 1.2, alpha: 0.35 });
  // gold diamonds down the centre
  const along = w >= h;
  const span = (along ? w : h) - 60;
  const n = Math.max(2, Math.floor(span / 90));
  for (let i = 0; i <= n; i++) {
    const o = -span / 2 + (span * i) / n;
    const m = (along ? h : w) * 0.16;
    if (along) g.poly([o, -m, o + m, 0, o, m, o - m, 0]).fill({ color: gold(art), alpha: 0.22 });
    else g.poly([-m, o, 0, o + m, m, o, 0, o - m]).fill({ color: gold(art), alpha: 0.22 });
  }
  c.addChild(g);
  return c;
}
